import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Functions } from '@hub/common/Functions';
import { eSearchContactSubType, eSearchContactType } from '@hub/generated/enums';
import { ContactCardDto, SearchContactsOptions } from '@hub/generated/models';
import { SearchController } from '@hub/generated/services';

interface IContactTypeTab {
  label: string;
  contactTypeId: number;
  types: eSearchContactType[];
  subTypes?: eSearchContactSubType[];
}

// contactTypeId values mirror the backend-only WME.Hub.ConnectService.ConnectEnumerators.eContactTypeConnect,
// which isn't exposed to Angular — these numbers must stay in sync with that enum.
const TYPE_TABS: IContactTypeTab[] = [
  { label: 'Client', contactTypeId: 1, types: [eSearchContactType.Client] },
  { label: 'Employee', contactTypeId: 2, types: [eSearchContactType.User] },
  { label: 'Buyer', contactTypeId: 3, types: [eSearchContactType.Buyer] },
  { label: 'Generic', contactTypeId: 4, types: [eSearchContactType.Contact], subTypes: [eSearchContactSubType.Generic] },
  { label: 'Talent', contactTypeId: 5, types: [eSearchContactType.Contact], subTypes: [eSearchContactSubType.Talent] },
  { label: 'Venue', contactTypeId: 9, types: [eSearchContactType.Venue] },
  { label: 'Industry', contactTypeId: 10, types: [eSearchContactType.Contact], subTypes: [eSearchContactSubType.Industry] }
];

@Component({
  selector: 'hub-advanced-contact-search-dialog',
  templateUrl: './advanced-contact-search-dialog.component.html',
  styleUrls: ['./advanced-contact-search-dialog.component.scss']
})
export class AdvancedContactSearchDialogComponent {
  constructor(private searchController: SearchController) { }

  @Input() visible: boolean = false;
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter();

  @Input() initialSearchText: string = '';
  @Input() initialContactTypeId: number = null;
  @Output() contactSelected: EventEmitter<ContactCardDto> = new EventEmitter();

  tabs: IContactTypeTab[] = TYPE_TABS;
  activeTab: IContactTypeTab = null;
  typeCounts: { ContactTypeId: number; ContactType: string; Count: number }[] = [];
  allCount: number = 0;

  rowsPerPageOptions: number[] = [50, 100, 200, 300];

  filters: SearchContactsOptions = Functions.GetInstanceSearchContactsOptions('');
  results: ContactCardDto[] = [];
  selectedContact: ContactCardDto = null;
  totalCount: number = 0;
  isLoading: boolean = false;

  page: number = 1;
  pageSize: number = this.rowsPerPageOptions[0];

  onDialogShow() {
    this.filters = Functions.GetInstanceSearchContactsOptions(this.initialSearchText || '');
    this.activeTab = this.tabs.find(tab => tab.contactTypeId === this.initialContactTypeId) || null;
    setTimeout(() => this.search());
  }

  selectTab(tab: IContactTypeTab) {
    this.activeTab = tab;
    this.selectedContact = null;
    this.page = 1;
    this.loadResults();
  }

  getTabCount(contactTypeId: number): number {
    const match = this.typeCounts.find(t => t.ContactTypeId === contactTypeId);
    return match ? match.Count : 0;
  }

  search() {
    this.page = 1;
    this.selectedContact = null;
    this.loadResults();
    this.loadCounts();
  }

  reset() {
    this.filters = Functions.GetInstanceSearchContactsOptions('');
    this.activeTab = null;
    this.search();
  }

  onLazyLoad(event: any) {
    this.pageSize = event.rows;
    this.page = Math.floor(event.first / event.rows) + 1;
    this.selectedContact = null;
    this.loadResults();
  }

  pickContact(contact: ContactCardDto) {
    this.selectedContact = this.selectedContact === contact ? null : contact;
  }

  confirmSelection() {
    if (!this.selectedContact) {
      return;
    }

    this.contactSelected.emit(this.selectedContact);
    this.close();
  }

  close() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  private buildOptions(): SearchContactsOptions {
    return {
      ...this.filters,
      Types: this.activeTab ? this.activeTab.types : [],
      ContactSubTypes: this.activeTab && this.activeTab.subTypes ? this.activeTab.subTypes : []
    };
  }

  private loadResults() {
    const options: SearchContactsOptions = { ...this.buildOptions(), Page: this.page, PageSize: this.pageSize };

    this.isLoading = true;
    this.searchController.SearchConnectContactsAdvanced(options).subscribe(
      result => {
        this.results = result.Contacts;
        this.totalCount = result.TotalCount;
        this.isLoading = false;
      },
      () => this.isLoading = false
    );
  }

  private loadCounts() {
    const options: SearchContactsOptions = { ...this.filters, Types: [], ContactSubTypes: [], Page: 1, PageSize: 1 };

    this.searchController.SearchConnectContactsAdvanced(options).subscribe(result => {
      this.typeCounts = result.Types;
      this.allCount = result.TotalCount;
    });
  }
}
